import httpClient from './base-service'
import paymentService from './payment.service'

export interface OrderConfirmationData {
  preOrderId: number
  posOrderId?: string
  posSystemType: string
  responseTimeMs: number
  establishmentId: number
  posTableId: number
  customerName: string
  subtotalAmount: number
  taxAmount: number
  tipAmount: number
  tipPercentage: number
  finalAmount: number
  orderStatus: string
  paymentStatus: string
  orderDate: string
  orderTrackingToken: string
  orderContainsFasTabOrder?: boolean
  items: OrderConfirmationItem[]
}

export interface OrderConfirmationItem {
  preItemId?: number
  menuItemId: number
  itemName: string
  itemDescription?: string
  unitPrice: number
  quantity: number
  totalPrice: number
  specialInstructions?: string
  customerSessionId?: string // To distinguish which customer ordered this item
  customerName?: string // Customer name for UI display
  seatNumber?: number // Seat number for UI display
  isPaid?: boolean // Indicates if the item has been paid
  isFasTabOrder?: boolean // Indicates if the item is from a FasTab order (pre_order)
  modifiers: OrderConfirmationModifier[]
}

export interface OrderConfirmationModifier {
  preItemAddOnModifierId?: number
  modifierName: string
  modifierDescription?: string
  modifierType: string
  modifierPrice: number
  quantity: number
  totalPrice: number
}

export interface OrderConfirmationResponse {
  success: boolean
  message: string
  data?: OrderConfirmationData
  error?: string
  timestamp: string
}

// OrderStatusResponse interface removed - order status is now handled by:
// 1. SignalR real-time updates (useSignalR hook)
// 2. Order status checking (instant correlation)
// 3. Payment processing completion handlers

export interface TipUpdateRequest {
  tipAmount: number
}

// Type for cached tab data - using unknown to avoid complex type matching issues
type CachedTabData = unknown

export class OrderConfirmationService {
  // Cache for tab data to reduce API calls
  private static tabDataCache: {
    data: CachedTabData
    timestamp: number
    tableId: number
    establishmentId: number
  } | null = null

  private static readonly CACHE_DURATION = 5000 // 5 seconds cache

  /**
   * Get payment intent totals (subtotal, tax, tip) and item IDs using backend session management
   * Used for "Your Tab Total" display and highlighting items in table summary
   * Backend uses rotatingGuid cookie internally to find the correct payment intent
   */
  static async getPaymentIntentTotals(
    tableId: number,
    establishmentId: number
  ): Promise<{
    success: boolean
    data: {
      paymentIntentId: string | null
      subtotal: number
      tax: number
      tip: number
      total: number
      itemIds: number[]
      itemCount: number
    } | null
    message?: string
  }> {
    try {
      const response = await httpClient.post(
        '/Tabs/get-payment-intent-totals',
        {
          posTableId: tableId,
          locationId: establishmentId,
        }
      )

      const data = response.data?.data || null

      return {
        success: true,
        data,
        message: 'Payment intent totals retrieved successfully',
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching payment intent totals:', error)
      return {
        success: false,
        data: null,
        message: 'Failed to fetch payment intent totals',
      }
    }
  }

  /**
   * Get full table order data using get-tab-order-by-id API
   * This returns all items for the table, not filtered by payment intent
   */
  static async getTabOrderById(
    tableId: number,
    establishmentId: number
  ): Promise<{ success: boolean; data: unknown; message?: string }> {
    try {
      const response = await httpClient.post('/Tabs/get-tab-order-by-id', {
        posTableId: tableId,
        locationId: establishmentId,
      })

      return {
        success: true,
        data: response.data?.data || null,
        message: 'Table order data retrieved successfully',
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching table order data:', error)
      return {
        success: false,
        data: null,
        message: 'Failed to fetch table order data',
      }
    }
  }

  // checkOrderStatus method removed - order status is now handled by:
  // 1. SignalR real-time updates (useSignalR hook)
  // 2. Order status checking (instant correlation)
  // 3. Payment processing completion handlers

  /**
   * Get cached tab data or fetch fresh data if cache is stale
   * This reduces duplicate API calls across components
   */
  private static async getCachedTabData(
    tableId: number,
    establishmentId: number
  ): Promise<CachedTabData> {
    const now = Date.now()

    // Check if we have valid cached data
    if (
      OrderConfirmationService.tabDataCache &&
      OrderConfirmationService.tabDataCache.tableId === tableId &&
      OrderConfirmationService.tabDataCache.establishmentId ===
        establishmentId &&
      now - OrderConfirmationService.tabDataCache.timestamp <
        OrderConfirmationService.CACHE_DURATION
    ) {
      console.log('🚀 Using cached tab data (performance optimization)')
      return OrderConfirmationService.tabDataCache.data
    }

    // Cache is stale or doesn't exist, fetch fresh data
    console.log('📡 Fetching fresh tab data (cache miss or stale)')
    const { default: tabService } = await import('./tab.service')

    const tabRes = await tabService.getTabs({
      posTableId: tableId,
      locationId: establishmentId,
    })

    const tabData = tabRes?.data?.data
    if (tabData) {
      // Update cache
      OrderConfirmationService.tabDataCache = {
        data: tabData,
        timestamp: now,
        tableId,
        establishmentId,
      }
    }

    return tabData
  }

  /**
   * Check if user has active FasTab orders by querying the backend (with caching)
   * This replaces the unreliable cookie-based approach
   */
  static async hasActiveOrder(): Promise<boolean> {
    try {
      const tableId = sessionStorage.getItem('posTableId')
      const establishmentId = sessionStorage.getItem('establishmentId')

      if (!tableId || !establishmentId) {
        console.warn(
          'Missing tableId or establishmentId for active order check'
        )
        return false
      }

      const tabData = await OrderConfirmationService.getCachedTabData(
        Number(tableId),
        Number(establishmentId)
      )
      if (!tabData) {
        console.warn('No tab data received for active order check')
        return false
      }

      // Use the same logic as YourTabWrapper
      const typedTabData = tabData as {
        orders?: Array<{
          items?: Array<{
            isFasTabOrder?: boolean
          }>
        }>
        orderContainsFasTabOrder?: boolean
      }

      const hasItemsWithFasTabOrder =
        typedTabData.orders && typedTabData.orders.length > 0
          ? typedTabData.orders.some((order) =>
              order.items && order.items.length > 0
                ? order.items.some((item) => item.isFasTabOrder === true)
                : false
            )
          : false

      const hasFasTabOrder =
        hasItemsWithFasTabOrder ||
        typedTabData.orderContainsFasTabOrder ||
        false

      console.log('🔍 Active order check result:', {
        hasFasTabOrder,
        hasItemsWithFasTabOrder,
        ordersCount: typedTabData.orders?.length || 0,
        orderContainsFasTabOrder: typedTabData.orderContainsFasTabOrder,
        cached: OrderConfirmationService.tabDataCache !== null,
      })

      return hasFasTabOrder
    } catch (error) {
      console.error('Error checking for active orders:', error)
      return false
    }
  }

  /**
   * Calculate new total when tip changes
   */
  static calculateNewTotal(
    subtotal: number,
    tax: number,
    newTip: number
  ): number {
    return subtotal + tax + newTip
  }

  /**
   * Format currency for display
   */
  static formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`
  }

  /**
   * Validate tip amount (must be >= 0)
   */
  static validateTipAmount(amount: number): boolean {
    return amount >= 0
  }

  // checkAndCleanupCompletedOrder method removed - cleanup is now handled by:
  // 1. SignalR real-time updates when orders are completed
  // 2. Payment processing completion handlers
  // 3. Order status polling hooks

  /**
   * Get payment method details from existing payment intent using rotatingGuid cookie
   * Returns customer info and card details for display
   */
  static async getPaymentMethodDetails(): Promise<{
    success: boolean
    data?: {
      type: string // "card", "apple_pay", "google_pay"
      last4?: string // Last 4 digits of card
      brand?: string // Card brand (visa, mastercard, etc.)
      customerName?: string
      emailAddress?: string
      firstName?: string
      lastName?: string
      postalCode?: string
    }
    message?: string
  }> {
    try {
      // Get table and establishment info from sessionStorage for proper order lookup
      const tableIdStr = sessionStorage.getItem('posTableId')
      const establishmentIdStr = sessionStorage.getItem('establishmentId')

      if (!tableIdStr || !establishmentIdStr) {
        console.warn(
          'Missing table or establishment ID for payment method details lookup'
        )
        return {
          success: false,
          data: null,
          message: 'Missing table or establishment information',
        }
      }

      const posTableId = parseInt(tableIdStr, 10)
      const establishmentId = parseInt(establishmentIdStr, 10)

      // Call backend with table/establishment IDs for rotatingGuid-based order lookup
      const response = await paymentService.getPaymentMethodDetails({
        posTableId,
        establishmentId,
      })

      // Handle both success and "no active session" scenarios as 200 OK responses
      if (response.status === 200) {
        const responseData = response.data?.data || response.data

        // Check if backend returned success: false (no active session)
        if (responseData.success === false) {
          return {
            success: false,
            data: null,
            message: responseData.message || 'No active payment method found',
          }
        }

        // Backend returned actual payment method data
        return {
          success: true,
          data: responseData,
          message: 'Payment method details retrieved successfully',
        }
      }

      return {
        success: false,
        data: null,
        message: 'Unexpected response from server',
      }
    } catch (error) {
      console.error('Error fetching payment method details:', error)
      return {
        success: false,
        data: null,
        message: 'Failed to fetch payment method details',
      }
    }
  }

  /**
   * Close tab - validates timing and processes payment directly using existing payment intent
   */
  async closeTab(
    tableId: number,
    establishmentId: number,
    totalAmount: number,
    tipAmount?: number
  ): Promise<{
    success: boolean
    message: string
    canClose?: boolean
    paymentProcessed?: boolean
    amountCharged?: number
    isTimingValidation?: boolean
  }> {
    try {
      const response = await httpClient.post('/order/close-tab', {
        posTableId: tableId,
        establishmentId,
        totalAmount,
        tipAmount,
      })

      const responseData = response.data?.data || {}

      // Handle timing validation responses (now returned as 200 with specific flags)
      if (responseData.isTimingValidation === true) {
        return {
          success: false,
          canClose: false,
          message:
            responseData.message || 'Please wait before closing your tab.',
          isTimingValidation: true,
        }
      }

      return responseData.success !== undefined
        ? responseData
        : {
            success: false,
            message: 'Unable to close tab',
          }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${this.constructor.name}: Error closing tab:`, error)

      // Check if it's an axios error with response data (like business logic validation errors)
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: {
            status?: number
            data?: {
              message?: string
              data?: string
              error?: string
            }
          }
        }

        if (axiosError.response?.status === 400 && axiosError.response.data) {
          // Try different possible message fields in order of preference
          const responseData = axiosError.response.data as {
            data?: string
            message?: string
            error?: string
          }
          const backendMessage =
            responseData.data || responseData.message || responseData.error

          if (backendMessage && typeof backendMessage === 'string') {
            return {
              success: false,
              message: backendMessage,
              canClose: false,
            }
          }
        }
      }

      return {
        success: false,
        message: 'Unable to close tab due to network error',
      }
    }
  }
}
